import React, { useState, useEffect, useRef } from 'react';
import { Button, Table, message, Progress, Space } from 'antd';

const OrgMigration = ({ db }) => {
    const [orgs, setOrgs] = useState([]);
    const [orgLoading, setOrgLoading] = useState(false);
    const [orgMigrating, setOrgMigrating] = useState(false);
    const [orgProgress, setOrgProgress] = useState(0);
    const [orgPaused, setOrgPaused] = useState(false);
    const [orgStopped, setOrgStopped] = useState(false);
    const orgPausedRef = useRef(false);
    const orgStoppedRef = useRef(false);

    useEffect(() => { orgPausedRef.current = orgPaused; }, [orgPaused]);
    useEffect(() => { orgStoppedRef.current = orgStopped; }, [orgStopped]);

    const waitIfPaused = async (pRef, sRef) => {
        while (pRef.current && !sRef.current) {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
    };

    const fetchOrgs = async () => {
        setOrgLoading(true);
        try {
            const snapshot = await db.collection('organizations').where('buyer', '==', true).get();
            const orgsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setOrgs(orgsData);
        } catch (error) {
            message.error("Failed to fetch organizations");
        }
        setOrgLoading(false);
    };

    useEffect(() => {
        fetchOrgs();
    }, []);

    const migrateAllOrgs = async () => {
        setOrgMigrating(true);
        setOrgStopped(false);
        setOrgPaused(false);
        orgStoppedRef.current = false;
        orgPausedRef.current = false;
        setOrgProgress(0);

        const orgsToMigrate = orgs.filter(org => org.seller);
        const total = orgsToMigrate.length;
        if (total === 0) { message.info("No buyer orgs need migration"); setOrgMigrating(false); return; }

        let processed = 0;
        for (const org of orgsToMigrate) {
            if (orgStoppedRef.current) break;
            await waitIfPaused(orgPausedRef, orgStoppedRef);
            if (orgStoppedRef.current) break;

            try {
                if (org.seller && (!org.resellers || org.resellers.length === 0)) {
                    await db.collection('organizations').doc(org.id).update({
                        resellers: [{
                            organization: org.seller,
                            name: org.seller_name || ''
                        }]
                    });
                }
            } catch (error) { console.error(error); }
            processed++;
            setOrgProgress(Math.round((processed / total) * 100));
        }
        setOrgMigrating(false);
        message[orgStoppedRef.current ? 'warning' : 'success'](orgStoppedRef.current ? "Stopped" : "Completed");
        fetchOrgs();
    };

    const columnsOrgs = [
        { title: 'Org ID', dataIndex: 'id', key: 'id' },
        { title: 'Org Name', dataIndex: 'name', key: 'name' },
        { title: 'Seller ID', dataIndex: 'seller', key: 'seller' },
        {
            title: 'Multi Seller Status',
            key: 'multi_seller',
            render: (_, record) => record.resellers && record.resellers.length > 0 ? <span style={{ color: 'green' }}>Converted</span> : <span style={{ color: 'orange' }}>Pending</span>
        }
    ];

    return (
        <>
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>List of organizations and their seller migration status</span>
                <Space>
                    {orgMigrating ? (
                        <>
                            <Button onClick={() => setOrgPaused(!orgPaused)}>{orgPaused ? "Resume" : "Pause"}</Button>
                            <Button danger onClick={() => { setOrgStopped(true); orgStoppedRef.current = true; }}>Stop</Button>
                        </>
                    ) : (
                        <Button type="primary" onClick={migrateAllOrgs}>Migrate All</Button>
                    )}
                </Space>
            </div>
            {orgMigrating && <Progress percent={orgProgress} status={orgPaused ? "active" : "normal"} style={{ marginBottom: '16px' }} />}
            <Table dataSource={orgs} columns={columnsOrgs} rowKey="id" loading={orgLoading} pagination={{ pageSize: 15 }} />
        </>
    );
};

export default OrgMigration;
